/**
 * Provides the `getTokenParts` utility function for splitting a token ID string by colons (`:`) while preserving colons that appear inside nested tokens (e.g., `{{inner:token}}`), and its associated `GetTokenPartsOptions` type.
 * @module getTokenParts
 */
// Import global utils
import { getTokenIDsFromString } from '@automator/shared/utils/get-token-id-from-string';
import { isEmpty } from '@automator/shared/utils/is-empty';
/**
 * Splits a token ID string by colons (`:`) while preserving colons that appear inside nested tokens (e.g., `{{inner:token}}`).
 *
 * **Behavior:**
 * - Takes a `tokenID` string as input. Handles `null`, `undefined`, and empty strings by returning an empty array `[]`.
 * - **Step 1: Identify Nested Tokens:** Uses `getTokenIDsFromString` to find the content of all top-level `{{...}}` structures within the `tokenID`.
 * - **Step 2: Placeholder Replacement:** If nested tokens are found, it iterates through them:
 *  - Reconstructs the full nested token string (e.g., `{{content}}`).
 *  - Creates a unique, temporary placeholder string (e.g., `%%PLACEHOLDER_0%%`).
 *  - Replaces the *first occurrence* of the full nested token in the `tokenID` string with the placeholder.
 *  - Stores a mapping from the placeholder back to the original full nested token.
 * - **Step 3: Split by Colon:** Splits the modified `tokenID` string (now containing placeholders instead of nested tokens) by the colon (`:`) delimiter.
 * - **Step 4: Restore Nested Tokens:** Iterates through the resulting parts from the split:
 *  - For each part, replaces any placeholders found back with their original full nested token string using the stored map.
 * - Returns the final array of parts, where colons inside the original nested tokens have been preserved.
 * - **Optimization:** If Step 1 finds no nested tokens (`{{...}}`), the function skips the placeholder steps (2 & 4) and directly splits the original `tokenID` by colons for efficiency.
 *
 * @since 6.3 Initial implementation.
 * @param {string | null | undefined} tokenID - The token string to parse, potentially containing colons and nested `{{...}}` tokens. Can be null or undefined.
 * @return {string[]} An array of strings representing the parts of the `tokenID` split by colons, with nested tokens treated as atomic units. Returns an empty array `[]` if the input is null, undefined, or an empty string.
 *
 * @example Importing the getTokenParts utility
 * ```typescript
 * import { getTokenParts } from '@automator/shared/utils/get-token-parts';
 * ```
 *
 * @example Simple token splitting
 * Splits a string with no nested tokens.
 * ```typescript
 * const tokenID = 'PART_1:PART_2:PART_3';
 * getTokenParts( tokenID );
 * // => [ 'PART_1', 'PART_2', 'PART_3' ]
 * ```
 *
 * @example Ignoring colons inside nested tokens
 * Splits the string by colons but keeps the content within `{{...}}` intact, including internal colons.
 * ```typescript
 * const tokenID = 'UT:INTEGRATION_CODE:TOKEN_CODE:{{UT:CODE_1:FIELD_1}}';
 * getTokenParts( tokenID );
 * // => [ 'UT', 'INTEGRATION_CODE', 'TOKEN_CODE', '{{UT:CODE_1:FIELD_1}}' ]
 * ```
 *
 * @example Handling multiple nested tokens
 * Correctly identifies and preserves multiple separate nested tokens.
 * ```typescript
 * const tokenID = 'PREFIX:{{A:B}}:MIDDLE:{{C:D:E}}';
 * getTokenParts( tokenID );
 * // => [ 'PREFIX', '{{A:B}}', 'MIDDLE', '{{C:D:E}}' ]
 * ```
 *
 * @example Handling duplicate nested tokens
 * Preserves both instances of a repeated nested token correctly.
 * ```typescript
 * const tokenID = 'FIRST:{{USER:ID}}:MIDDLE:{{USER:ID}}:LAST';
 * getTokenParts( tokenID );
 * // => [ 'FIRST', '{{USER:ID}}', 'MIDDLE', '{{USER:ID}}', 'LAST' ]
 * ```
 *
 * @example Handling deeply nested structures
 * Preserves deeply nested braces and internal colons within the top-level token.
 * ```typescript
 * const tokenID = 'OUTER:{{LEVEL1:{{LEVEL2:VALUE}}}}';
 * getTokenParts( tokenID );
 * // => [ 'OUTER', '{{LEVEL1:{{LEVEL2:VALUE}}}}' ]
 * ```
 *
 * @example Handling empty parts
 * Correctly handles empty strings resulting from adjacent colons.
 * ```typescript
 * const tokenID = 'PART_A::PART_C';
 * getTokenParts( tokenID );
 * // => [ 'PART_A', '', 'PART_C' ]
 * ```
 *
 * @example Handling null or empty input
 * Returns an empty array for null, undefined, or empty string inputs.
 * ```typescript
 * getTokenParts( null );
 * // => []
 * getTokenParts( '' );
 * // => []
 * ```
 *
 * @remarks
 * - This function relies on the `getTokenIDsFromString` utility to correctly identify the boundaries of top-level nested tokens.
 * - The placeholder mechanism (`%%PLACEHOLDER_n%%`) assumes these placeholder strings are highly unlikely to appear naturally in the input `tokenID`.
 * - The `replace` method used for placeholders only replaces the first occurrence if duplicates exist *before* splitting. However, after splitting, each part is processed independently, ensuring all placeholders are restored correctly within their respective parts.
 * - For comprehensive behavior details, refer to the unit tests.
 */
export function getTokenParts(tokenID) {
    // If the tokenID is empty, return an empty array
    if (isEmpty(tokenID)) {
        return [];
    }
    // At this point, tokenID is a non-empty string.
    // Step 1: Extract the contents of top-level nested tokens (e.g., "NESTED:TOKEN" from "{{NESTED:TOKEN}}")
    const tokens = getTokenIDsFromString(tokenID);
    // Optimization: If there are no nested tokens, just split normally by colon.
    if (tokens.length === 0) {
        return tokenID.split(':');
    }
    // Step 2: Replace full nested tokens (e.g., "{{NESTED:TOKEN}}") in the original string with placeholders
    let placeholderID = 0; // Unique ID for placeholders
    const placeholderMap = new Map(); // Map to store placeholder -> original token mapping
    let cleanedTokenID = tokenID; // Work with a copy
    // Use regular expressions to replace the nested tokens with placeholders only when surrounded by colons
    tokens.forEach((tokenContent) => {
        // Reconstruct the full token string (e.g., "{{NESTED:TOKEN}}")
        const fullToken = `{{${tokenContent}}}`;
        // Create a unique placeholder (less likely to collide with actual content)
        const placeholder = `%%PLACEHOLDER_${placeholderID++}%%`;
        // Replace the first occurrence of the full token with its placeholder.
        cleanedTokenID = cleanedTokenID.replace(fullToken, placeholder);
        // Store the mapping for later restoration
        placeholderMap.set(placeholder, fullToken);
    });
    // Step 3: Split the placeholder-filled string by the colon delimiter.
    // This works because the placeholders %%PLACEHOLDER_n%% do not contain colons.
    const rawParts = cleanedTokenID.split(':');
    // Step 4: Restore the original nested tokens by replacing placeholders in each part.
    const finalParts = rawParts.map((part) => {
        let restoredPart = part;
        placeholderMap.forEach((originalToken, placeholder) => {
            // Replace placeholder back with the original full token string.
            // String.replace handles cases where placeholder isn't found.
            restoredPart = restoredPart.replace(placeholder, originalToken);
        });
        return restoredPart;
    });
    return finalParts; // Return the array of parts with nested tokens restored
}
;
